import * as React from "react";
import {
    Input,
    ColumnLayout
} from "@amzn/awsui-components-react";
import {
    AdditionalFieldConfig,
    AdditionalFieldValue,
    ConfigurableOption
} from "@amzn/ask-legal-domain";
import { UIField } from "../common/UIField";

export interface AdditionalFieldsComponentProps {
    additionalFieldsConfig: AdditionalFieldConfig[];
    additionalFieldsValues: { [fieldKey: string]: AdditionalFieldValue };
    onAdditionalFieldChange: (fieldKey: string, value: AdditionalFieldValue) => void;
    disabled?: boolean;
    fieldErrors?: { [fieldKey: string]: string };
    ConfigurableOptionMultiselectWithFreeText?: React.ComponentType<any>;
}

export const AdditionalFieldsComponent: React.FC<AdditionalFieldsComponentProps> = ({
    additionalFieldsConfig,
    additionalFieldsValues,
    onAdditionalFieldChange,
    disabled = false,
    fieldErrors = {},
    ConfigurableOptionMultiselectWithFreeText
}) => {
    const renderChoiceField = (fieldConfig: AdditionalFieldConfig) => {
        const fieldValue = additionalFieldsValues[fieldConfig.fieldKey];
        const choiceValues = Array.isArray(fieldValue) ? fieldValue : [];
        const options = fieldConfig.choices || [];

        // Filter out deprecated choices
        const activeOptions = options.filter(option => !option.deprecated);

        return (
            <UIField.CustomField
                key={fieldConfig.fieldKey}
                name={fieldConfig.name}
                errorText={fieldErrors[fieldConfig.fieldKey]}
                child={
                    <ConfigurableOptionMultiselectWithFreeText
                        configurableOptionsEditState={{
                            value: choiceValues,
                            setValue: (value: ConfigurableOption[]) => {
                                onAdditionalFieldChange(fieldConfig.fieldKey, value);
                            },
                            errorText: undefined,
                            reset: () => {}
                        }}
                        options={activeOptions}
                        loadingOptions={false}
                    />
                }
            />
        );
    };

    const renderLinkField = (fieldConfig: AdditionalFieldConfig) => {
        const fieldValue = additionalFieldsValues[fieldConfig.fieldKey];

        return (
            <UIField.CustomField
                key={fieldConfig.fieldKey}
                name={fieldConfig.name}
                errorText={fieldErrors[fieldConfig.fieldKey]}
                child={
                    <Input
                        value={fieldValue || ""}
                        onChange={({ detail }) => {
                            const newValue = detail.value.trim();
                            onAdditionalFieldChange(fieldConfig.fieldKey, newValue);
                        }}
                        placeholder={`Enter ${fieldConfig.name} URL`}
                        type="url"
                        disabled={disabled}
                    />
                }
            />
        );
    };

    const renderField = (fieldConfig: AdditionalFieldConfig) => {
        switch (fieldConfig.type) {
            case "choice":
                return renderChoiceField(fieldConfig);
            case "links":
                return renderLinkField(fieldConfig);
            default:
                return null;
        }
    };

    if (additionalFieldsConfig.length === 0) {
        return null;
    }

    return (
        <ColumnLayout columns={2}>
            {additionalFieldsConfig.map(renderField)}
        </ColumnLayout>
    );
};
